/**
 * Endpoint Test types (task 365).
 *
 * An EndpointTest is a saved, reusable input for an endpoint (AI Service):
 * either captured manually from the runner ("Save as test") or proposed by the
 * seeded `compose-endpoint-tests` generator and explicitly saved by the User.
 * Running a test records an EndpointTestRun linking to the produced
 * Collaboration, so the existing run detail page renders the result.
 *
 * Backend contracts: CRUD + run API (task 363, `/api/endpoint-tests`) and the
 * generation service (task 364, dynamic endpoint `compose-endpoint-tests` —
 * same synchronous envelope as the other platform generators).
 */

import type { AuditFields, RunCost } from './index';

/**
 * How a test came to exist: proposed by the generator, saved or typed by hand,
 * brought in from a file (task 1175), or proposed by red teaming (follow-on
 * report). Mirrors innovate-service's validator enum (task 1171).
 */
export type EndpointTestSource = 'generated' | 'manual' | 'imported' | 'red-team';

/** Compact last-run summary included on list rows when the test has run. */
export interface EndpointTestLastRun {
  status: string;
  createdAt: string;
}

/** A saved test for an endpoint (account-scoped, soft-deleted like all entities). */
export interface EndpointTest extends AuditFields {
  id: string;
  endpointId: string;
  accountId?: string;
  name: string;
  description?: string | null;
  /** Optional free-text expectation the User records for reviewers. */
  expectedOutcome?: string | null;
  /** The saved request input, conforming to the endpoint's input schema. */
  input: Record<string, unknown>;
  source: EndpointTestSource;
  /** Present on list rows when the test has at least one recorded run. */
  lastRun?: EndpointTestLastRun | null;
}

/** One recorded execution of a test, linking to the produced Collaboration. */
export interface EndpointTestRun extends AuditFields {
  id: string;
  endpointTestId: string;
  accountId?: string;
  collaborationId: string | null;
  requestId: string | null;
  status: string;
}

/** Body for POST /api/endpoint-tests. */
export interface CreateEndpointTestInput {
  endpointId: string;
  name: string;
  description?: string;
  input: Record<string, unknown>;
  expectedOutcome?: string;
  source: EndpointTestSource;
}

/** Body for PATCH /api/endpoint-tests/:id. */
export interface UpdateEndpointTestInput {
  name?: string;
  description?: string | null;
  expectedOutcome?: string | null;
  input?: Record<string, unknown>;
}

/**
 * Result of POST /api/endpoint-tests/:id/run — the recorded run plus the
 * endpoint's output. Kept permissive so newer backend builds can add fields.
 */
export interface RunEndpointTestResult {
  run: EndpointTestRun;
  output?: unknown;
  [key: string]: unknown;
}

// ─── Generation (compose-endpoint-tests) ─────────────────────────────────────

export type TestScenarioCategory = 'typical' | 'edge' | 'boundary';

/**
 * One test scenario the generator proposed — and, since task 795, saved.
 *
 * The scenario is the DISPLAY shape: what the generator wrote. Its saved
 * counterpart arrives beside it as a `SavedTestReference`, matched by name.
 */
export interface TestScenario {
  name: string;
  description: string;
  category: TestScenarioCategory;
  /** Proposed request input, conforming to the endpoint's input schema. */
  input: Record<string, unknown>;
  rationale: string;
}

/** The endpoint definition fields the generator needs (attached automatically —
 * never typed by the User). */
export interface ComposeTestsEndpointSpec {
  path: string;
  /**
   * Owning account slug of the endpoint under test (task 385) — the generated
   * scenarios reference `/api/<accountSlug>/<path>`.
   */
  accountSlug?: string | null;
  method: string;
  description?: string | null;
  input?: Record<string, unknown> | null;
  output?: Record<string, unknown> | null;
}

/** Request body for the compose-endpoint-tests generator. */
export interface ComposeTestsRequest {
  endpointSpec: ComposeTestsEndpointSpec;
  /**
   * The AI Service the generated tests are SAVED against (task 795). The spec
   * above describes the service to the generator; this addresses the rows the
   * collaboration's save step writes, and the service checks it against the
   * caller's account.
   */
  endpointId?: string;
  /** Optional free-text steering (e.g. "focus on multilingual threads"). */
  guidance?: string;
  /** Requested number of scenarios. */
  count?: number;
  /** Present on iterate turns to continue the same generation collaboration. */
  collaborationId?: string;
}

/**
 * A test the generation turn saved (task 795) — the reference the turn output
 * carries back, in place of content the caller was expected to persist.
 */
export interface SavedTestReference {
  id: string;
  name: string;
  /** Whether the turn opened this test or revised one it had already saved. */
  action: 'created' | 'updated';
}

/** The generator's output (rides back under `data.output`). */
export interface ComposeTestsOutput {
  scenarios: TestScenario[];
  /**
   * What the turn persisted (task 795). Every scenario above is saved as an
   * EndpointTest with `source: 'generated'` DURING the turn — the collaboration
   * owns the save, so there is nothing here for the caller to confirm.
   */
  savedTests?: SavedTestReference[];
  /** Why the scenarios were not saved, when they were not. */
  saveError?: string;
  [key: string]: unknown;
}

/** Shape of `data` returned by the synchronous compose-endpoint-tests invoke. */
export interface ComposeTestsResponse {
  requestId: string;
  collaborationId: string;
  status: string;
  output: ComposeTestsOutput | null;
}

// ─── Import / export (tasks 1173, 1175) ──────────────────────────────────────

/**
 * Where a file column lands on import: one of the service's top-level input
 * field keys, or one of the three test columns. Columns the User skips are
 * left out of the map entirely.
 */
export type ImportColumnTarget = string;

/** The three non-input test columns import recognises. */
export const IMPORT_TEST_COLUMNS = ['name', 'description', 'expected'] as const;

/** Body for POST /api/endpoint-tests/import (task 1173). */
export interface ImportEndpointTestsInput {
  endpointId: string;
  /** file column → input field key | name | description | expected */
  columnMap: Record<string, ImportColumnTarget>;
  /** Parsed rows: CSV cells are strings; JSON values arrive as written. */
  rows: Array<Record<string, unknown>>;
}

/** Result of POST /api/endpoint-tests/import. */
export interface ImportEndpointTestsResult {
  created: number;
  skipped: number;
}

export type EndpointTestExportFormat = 'csv' | 'json';

// ─── Test runs (EndpointTestRunGroup — tasks 1171, 1172, 1176, 1177) ─────────
//
// In the UI a group is a **test run**; the per-test `EndpointTestRun` rows
// inside it are its **results**; every check, graded criterion and human
// verdict on a result is a **score**. "Run" alone keeps meaning a collaboration
// execution, as everywhere else in the app.

/** Outcome a score records. */
export type EndpointTestScoreOutcome = 'pass' | 'fail' | 'uncertain';

/** `human` since Phase 1; `check` and `graded` arrive with criteria (task 1179). */
export type EndpointTestScoreKind = 'human' | 'check' | 'graded';

/** One outcome on a result. `createdBy`/`createdAt` ARE a human verdict's attribution. */
export interface EndpointTestRunScore extends AuditFields {
  id: string;
  endpointTestRunId: string;
  kind: EndpointTestScoreKind;
  /** Null for the human verdict. */
  endpointTestCriterionId?: string | null;
  /**
   * The criterion, joined. The two reads name the key differently (shipped
   * contract): `GET /endpoint-test-runs/:id/scores` uses `criterion` (with
   * `endpointTestId`), the test run detail uses `endpointTestCriterion`. Read
   * through `scoreCriterion()` in utils/testRunResults.
   */
  criterion?:
    | (Pick<EndpointTestCriterion, 'id' | 'name' | 'kind' | 'required'> & {
        endpointTestId?: string | null;
        /** The criterion's definition; a graded one carries its pass bar (task 1265). */
        definition?: EndpointTestCriterion['definition'];
      })
    | null;
  endpointTestCriterion?: Pick<EndpointTestCriterion, 'id' | 'name' | 'kind' | 'required'> | null;
  outcome: EndpointTestScoreOutcome;
  /** 0–1; null for the human verdict and for an uncertain graded verdict (task 1265). */
  score?: number | null;
  /** The grader's one-line rationale, or the human verdict's note. */
  rationale?: string | null;
  cached?: boolean;
}

/**
 * Counts behind the summary strip (list rows, comparison sides). The service
 * always sends `tests, passed, failed, uncertain, completed, errored, reviewed,
 * duration`; `pending` is computed client-side only (a test run stores no
 * planned test list, so in-flight = requested − results).
 */
export interface EndpointTestRunGroupSummary {
  /** Results with a passing verdict (Phase 1: a human `pass`). */
  passed: number;
  /** Human `fail` (Phase 2: roll-up `fail`). */
  failed: number;
  /** Finished with no verdict yet — never shown as passed. */
  completed: number;
  /** Transport errors. */
  errored: number;
  /** Results carrying a human verdict. */
  reviewed: number;
  /** Roll-up `uncertain` with no human verdict. */
  uncertain: number;
  /** Client-side only: requested tests with no result yet. */
  pending?: number;
  /** Client-side only (task 1229): requested tests the service refused to run (`refusals`). */
  notRun?: number;
  /** Number of results in the test run. */
  tests?: number;
  /** Total duration in ms, summed from the linked collaborations. */
  duration?: number | null;
}

/** The environment a test run targeted, as the service joins it. */
export interface EndpointTestRunGroupEnvironment {
  id: string;
  name?: string;
  path: string;
}

/** The published version that served a test run (`resourceVersionId`). */
export interface EndpointTestRunGroupVersion {
  id?: string;
  major: number;
  minor: number;
}

/** One test run — `EndpointTestRunGroup`. */
export interface EndpointTestRunGroup extends AuditFields {
  id: string;
  endpointId: string;
  accountId?: string;
  /** Null = working state (same meaning as on Schedule). */
  environmentId: string | null;
  /** The ResourceVersion the environment's Deployment served; null for working state. */
  resourceVersionId: string | null;
  concurrency: number;
  graded: boolean;
  /** The test run this one re-ran from. */
  parentEndpointTestRunGroupId: string | null;
  environment?: EndpointTestRunGroupEnvironment | null;
  resourceVersion?: EndpointTestRunGroupVersion | null;
  summary?: EndpointTestRunGroupSummary;
  /**
   * The scenario ids requested, in request order, after `failedOnly` narrowing
   * (task 1228). Rows created before the column read `[]`.
   */
  testIds: string[];
  /** When the fan-out finished, whatever happened; null while the test is running (task 1228). */
  completedAt: string | null;
  /** Scenarios refused before a collaboration started; `[]` on a clean run, null while running (task 1228). */
  refusals: { testId: string; error: string }[] | null;
  /** The per-endpoint `#12` handle (task 1231); gaps are expected, never reused. */
  number?: number;
  /** Where the test came from (task 1231). */
  origin?: EndpointTestRunGroupOrigin;
  endpointTestSuiteId?: string | null;
  /** The suite's current name ("Suite (deleted)" once deleted); null when there is none. */
  endpointTestSuite?: { id: string; name: string; deletedAt?: string | null } | null;
  /** The results' runs priced and summed (task 1253), on the list read; null when any run is unpriced. */
  cost?: RunCost | null;
}

/** `EndpointTestRunGroup.origin` (task 1231). */
export type EndpointTestRunGroupOrigin = 'suite' | 'assistant' | 'selection' | 'single' | 'rerun' | 'rerun_failed';

/** The collaboration a result produced, as the detail route joins it. */
export interface EndpointTestRunCollaboration {
  id: string;
  status?: string;
  /** Execution time in ms — the result's duration (never stored on the result). */
  duration?: number | null;
}

/** A result's displayed outcome: human verdict, then criteria roll-up, then transport. */
export type EndpointTestResultOutcome = 'pass' | 'fail' | 'uncertain' | 'completed' | 'errored';

/** One result inside a test run: an `EndpointTestRun` with its test and scores. */
export interface EndpointTestRunResult extends EndpointTestRun {
  endpointTestRunGroupId?: string | null;
  endpointTest?: Pick<EndpointTest, 'id' | 'name'> & Partial<Pick<EndpointTest, 'input' | 'source' | 'expectedOutcome'>>;
  collaboration?: EndpointTestRunCollaboration | null;
  /** ms, from the linked collaboration (completedAt − startedAt); null while unknown. */
  duration?: number | null;
  /** The displayed outcome, as the service computes it. */
  outcome?: EndpointTestResultOutcome | null;
  /** The criteria roll-up alone (task 1179), or null when nothing gating was evaluated. */
  evaluatedOutcome?: EndpointTestScoreOutcome | null;
  /** The current human verdict, if any. */
  humanScore?: EndpointTestRunScore | null;
  scores?: EndpointTestRunScore[];
}

/** GET /api/endpoint-test-run-groups/:id — the group plus one row per result. */
export interface EndpointTestRunGroupDetail extends EndpointTestRunGroup {
  results: EndpointTestRunResult[];
}

/** Body for POST /api/endpoint-test-run-groups (task 1172). */
export interface CreateEndpointTestRunGroupInput {
  endpointId: string;
  testIds: string[];
  environmentId: string | null;
  concurrency: number;
  graded: boolean;
  parentEndpointTestRunGroupId?: string | null;
  failedOnly?: boolean;
  /**
   * Where the start came from (task 1231). Callers send one of these four;
   * `rerun` / `rerun_failed` are derived by the service from the parent id.
   */
  origin?: 'suite' | 'assistant' | 'selection' | 'single';
  /** Required with `origin: 'suite'`. */
  endpointTestSuiteId?: string | null;
}

/** 202 body of POST /api/endpoint-test-run-groups. */
export interface CreateEndpointTestRunGroupResult {
  id: string;
}

/** Body for PUT /api/endpoint-test-runs/:id/human-score. */
export interface HumanScoreInput {
  outcome: 'pass' | 'fail';
  rationale?: string;
}

/** One point of GET /api/endpoint-tests/:id/trend — newest first. */
export interface EndpointTestTrendPoint {
  endpointTestRunGroupId: string;
  outcome: string | null;
}

// ─── Comparison (tasks 1174, 1178; criteria deltas 1179, 1180) ───────────────

/** Outcome a comparison side reports per test. */
export type ComparisonOutcome = 'pass' | 'fail' | 'completed' | 'errored' | 'uncertain' | null;

export type ComparisonChange =
  | 'regressed'
  | 'improved'
  | 'unchanged'
  | 'flipped-criterion'
  | 'only-a'
  | 'only-b';

export interface ComparisonSide {
  id: string;
  environment: EndpointTestRunGroupEnvironment | null;
  resourceVersion: EndpointTestRunGroupVersion | null;
  summary: EndpointTestRunGroupSummary;
  createdAt?: string;
}

export interface ComparisonCell {
  outcome: ComparisonOutcome;
  endpointTestRunId: string | null;
}

export interface ComparisonRow {
  endpointTestId: string;
  name: string;
  a: ComparisonCell | null;
  b: ComparisonCell | null;
  change: ComparisonChange;
}

/** One side of a criterion's delta. */
export interface ComparisonCriterionSide {
  /** 0–1; null when the criterion did not run on this side. */
  passRate: number | null;
  meanScore: number | null;
  /** Results that scored this criterion (sides only). */
  count?: number;
}

/** Per-criterion pass rate / mean score in each test run (task 1179). */
export interface ComparisonCriterion {
  endpointTestCriterionId?: string;
  name: string;
  kind?: EndpointTestCriterionKind;
  required?: boolean;
  a: ComparisonCriterionSide;
  b: ComparisonCriterionSide;
  delta: ComparisonCriterionSide;
}

/** GET /api/endpoint-test-run-groups/compare?a=&b= */
export interface EndpointTestRunGroupComparison {
  a: ComparisonSide;
  b: ComparisonSide;
  rows: ComparisonRow[];
  criteria: ComparisonCriterion[];
}

/** GET /api/endpoint-test-run-groups/:id/compare-suggestion */
export interface CompareSuggestion {
  id: string | null;
}

// ─── Criteria (tasks 1179, 1180) ─────────────────────────────────────────────
//
// Named after what the Run Explorer already shows: **checks** (deterministic,
// free, always on) and **graded criteria** (model-graded, one grader call per
// result, cached).

export type EndpointTestCriterionKind = 'check' | 'graded';

export type CheckType = 'schema' | 'field-present' | 'field-equals' | 'field-contains';

export interface CheckDefinition {
  type: CheckType;
  path?: string;
  value?: string;
}

/**
 * A graded criterion (task 1265): every result is scored 0 to 1 and passes at
 * or above `passThreshold` (the service's default, 0.7, when absent).
 */
export interface GradedDefinition {
  rubric: string;
  passThreshold?: number;
}

/** How a criterion was created, mirroring `EndpointTestSource` (task 1187). */
export type EndpointTestCriterionSource = 'manual' | 'generated';

export interface EndpointTestCriterion extends AuditFields {
  id: string;
  endpointId: string;
  /** Null = service criterion (measured on every result of every test). */
  endpointTestId: string | null;
  kind: EndpointTestCriterionKind;
  name: string;
  definition: CheckDefinition | GradedDefinition;
  /** False = informational: scored and trended, never gating. */
  required: boolean;
  /**
   * How the criterion was created (task 1187): `generated` = saved by the
   * Assistant's Criteria Composer, shown as "suggested". Absent on a service
   * that predates the column, which reads as `manual`.
   */
  source?: EndpointTestCriterionSource;
}

/** Body for POST /endpoint-tests/:id/criteria and /endpoints/:id/test-criteria. */
export interface CreateEndpointTestCriterionInput {
  kind: EndpointTestCriterionKind;
  name: string;
  definition: CheckDefinition | GradedDefinition;
  required: boolean;
}

/** PATCH body: `kind` is fixed once a criterion is saved. */
export type UpdateEndpointTestCriterionInput = Partial<Omit<CreateEndpointTestCriterionInput, 'kind'>>;

// ─── Named suites (task 1180) ────────────────────────────────────────────────

/** A saved selection of tests ("the regression pack"). */
export interface EndpointTestSuite extends AuditFields {
  id: string;
  endpointId: string;
  name: string;
  testIds: string[];
}

export interface CreateEndpointTestSuiteInput {
  endpointId: string;
  name: string;
  testIds: string[];
}

export type UpdateEndpointTestSuiteInput = Partial<Pick<EndpointTestSuite, 'name' | 'testIds'>>;
